import React, { useMemo, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ExternalLink, Upload, X } from 'lucide-react';
import { Button } from './ui/button';
import { PaymentProviderLogo, PROVIDER_LIST, getProviderConfig } from './PaymentProviderLogo';
import SafeImage from './SafeImage';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function PaymentMethodForm({ method, onChange, compact = false }) {
  const m = method || {};
  const set = (field, value) => onChange({ ...m, [field]: value });
  const [uploadingQR, setUploadingQR] = useState(false);

  const provider = m.payment_provider || null;
  const providerCfg = PROVIDER_LIST.find(p => p.id === provider);

  // Auto-generate URL from handle
  const generatedUrl = useMemo(() => {
    if (!providerCfg?.urlTemplate || !m.handle) return null;
    const h = m.handle.trim();
    if (!h) return null;
    return providerCfg.urlTemplate(h);
  }, [provider, m.handle, providerCfg]);

  const effectiveUrl = m.url && !m.is_auto_generated ? m.url : (generatedUrl || m.url || '');

  const handleProviderChange = (newProvider) => {
    const cfg = PROVIDER_LIST.find(p => p.id === newProvider);
    const isZelle = newProvider === 'zelle';
    const isCustom = newProvider === 'custom';
    onChange({
      ...m,
      payment_provider: newProvider,
      method: cfg?.label || m.method,
      payment_method_type: isZelle ? 'direct' : 'link',
      handle: '',
      url: '',
      is_auto_generated: !isZelle && !isCustom,
      recipient_name: isZelle ? m.recipient_name : '',
      email_or_phone: isZelle ? m.email_or_phone : '',
    });
  };

  const handleHandleChange = (val) => {
    const newUrl = providerCfg?.urlTemplate && val.trim() ? providerCfg.urlTemplate(val.trim()) : '';
    onChange({ ...m, handle: val, url: newUrl, is_auto_generated: !!newUrl });
  };

  const inputCls = `mt-1 bg-[#050505] border-[#1A1A1A] text-[#F3EEE7] focus:border-[#D4AF37] ${compact ? 'text-sm' : ''}`;
  const labelCls = compact ? 'text-[#F3EEE7] text-sm' : 'text-[#EAE7E1]';
  const isZelle = provider === 'zelle';
  const isCustom = provider === 'custom';
  const hasHandle = providerCfg?.handleLabel;

  return (
    <div className="space-y-4">
      {/* Provider Selector */}
      <div>
        <Label className={labelCls}>Payment Provider *</Label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2" data-testid="provider-selector">
          {PROVIDER_LIST.map(p => {
            const active = provider === p.id;
            const cfg = getProviderConfig(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handleProviderChange(p.id)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-sm border-2 transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1A1A1A] hover:border-[#333]'}`}
                data-testid={`provider-${p.id}`}
              >
                <PaymentProviderLogo provider={p.id} size="md" />
                <span className={`text-xs font-semibold ${active ? 'text-[#EAE7E1]' : 'text-[#777]'}`}>{p.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Handle input (Venmo/CashApp/PayPal) */}
      {provider && hasHandle && (
        <div>
          <Label className={labelCls}>{providerCfg.handleLabel} *</Label>
          <Input
            value={m.handle || ''}
            onChange={(e) => handleHandleChange(e.target.value)}
            placeholder={providerCfg.handlePlaceholder}
            className={inputCls}
            data-testid="provider-handle-input"
          />
          {/* Live URL preview */}
          {effectiveUrl && (
            <div className="mt-2 flex items-center gap-2 text-xs" data-testid="url-preview">
              <ExternalLink className="w-3 h-3 text-[#C6A66B] flex-shrink-0" />
              <a href={effectiveUrl} target="_blank" rel="noopener noreferrer" className="text-[#C6A66B] hover:text-[#D4AF37] truncate underline decoration-dotted underline-offset-2">
                {effectiveUrl}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Custom URL input */}
      {isCustom && (
        <>
          <div>
            <Label className={labelCls}>Method Name *</Label>
            <Input
              value={m.method || ''}
              onChange={(e) => set('method', e.target.value)}
              placeholder="e.g., Bitcoin, Wire Transfer"
              className={inputCls}
              data-testid="custom-method-name"
            />
          </div>
          <div>
            <Label className={labelCls}>Payment URL</Label>
            <Input
              value={m.url || ''}
              onChange={(e) => onChange({ ...m, url: e.target.value, is_auto_generated: false })}
              placeholder="https://..."
              className={inputCls}
              data-testid="custom-url-input"
            />
          </div>
        </>
      )}

      {/* Zelle-specific fields */}
      {isZelle && (
        <>
          <div>
            <Label className={labelCls}>Recipient Name *</Label>
            <Input
              value={m.recipient_name || ''}
              onChange={(e) => set('recipient_name', e.target.value)}
              placeholder="John Smith"
              className={inputCls}
              data-testid="zelle-recipient-input"
            />
          </div>
          <div>
            <Label className={labelCls}>Email or Phone *</Label>
            <Input
              value={m.email_or_phone || ''}
              onChange={(e) => set('email_or_phone', e.target.value)}
              placeholder="john@email.com or (555) 123-4567"
              className={inputCls}
              data-testid="zelle-contact-input"
            />
          </div>
          <div>
            <Label className={labelCls}>Suggested Memo (optional)</Label>
            <Input
              value={m.suggested_memo || ''}
              onChange={(e) => set('suggested_memo', e.target.value)}
              placeholder="EventName - YourName"
              className={inputCls}
              data-testid="zelle-memo-input"
            />
          </div>
          <div>
            <Label className={labelCls}>QR Code Image (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              {m.qr_code_image ? (
                <div className="relative group">
                  <SafeImage src={`${BACKEND_URL}${m.qr_code_image}`} alt="Payment QR" className="h-16 w-16 rounded" imgClassName="bg-white p-0.5 object-contain border border-[#1A1A1A]" fallbackLabel="QR" data-testid="qr-preview" />
                  <button type="button" onClick={() => set('qr_code_image', '')} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#C44545] text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-qr"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploadingQR(true);
                    try {
                      const token = localStorage.getItem('session_token');
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
                      set('qr_code_image', res.data.url);
                    } catch { toast.error('Failed to upload QR image'); }
                    finally { setUploadingQR(false); }
                  }} className="hidden" id="qr-upload-field" data-testid="qr-file-input" />
                  <Button type="button" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-sm" onClick={() => document.getElementById('qr-upload-field').click()} disabled={uploadingQR} data-testid="upload-qr-button">
                    <Upload className="w-3 h-3 mr-1.5" />{uploadingQR ? 'Uploading...' : 'Upload QR'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Instructions (all providers) */}
      <div>
        <Label className={labelCls}>Instructions for guests (optional)</Label>
        <Textarea
          value={m.instructions || ''}
          onChange={(e) => set('instructions', e.target.value)}
          placeholder="Any notes for guests about this payment method"
          rows={2}
          className={inputCls}
          data-testid="method-instructions-input"
        />
      </div>
    </div>
  );
}

/**
 * Validate a payment method before saving.
 * Returns error string or null if valid.
 */
export function validatePaymentMethod(m) {
  if (!m.payment_provider) return 'Select a payment provider';
  const cfg = PROVIDER_LIST.find(p => p.id === m.payment_provider);
  if (cfg?.handleLabel && !m.handle?.trim()) return `Enter your ${cfg.handleLabel}`;
  if (m.payment_provider === 'zelle' && !m.email_or_phone?.trim()) return 'Enter email or phone for Zelle';
  if (m.payment_provider === 'custom' && !m.method?.trim()) return 'Enter a method name';
  return null;
}
